import React, { useContext } from 'react';
import styled from '@emotion/styled';
import { Button, Tooltip, Table, TablePaginationConfig, Modal, message } from 'antd';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import { ShowHideColumnFinalTile, TitleColumn } from './column';
import { PRODUCT } from '../../../constants/define/translation';
import { appStyles } from '../../../styles';
import { IndexColumn } from '../shared-column';
import { ShopType } from '../../../types/shop';
import { ConfirmAction } from '../../molecules/confirm/ConfirmAction';
import { MESSAGE_ERROR } from '../../../constants/message';
import { postLoginRepository } from '../../../repository/mytm/auth/loginRepository';
import { UserContext } from '../../../repository/context/userContext';

const { Column } = Table;

interface FinalTileTableProps {
  records: ShopType[];
  loading?: boolean;
  className?: string;
  onRefresh: () => void;
  setIsOpenModal: (boolean: Boolean) => void;
  setDataFinalTile: Function;
  pagination?: TablePaginationConfig;
  setLoading: (value: boolean) => void;
}

export const FinalTileTable = (props: FinalTileTableProps) => {
  const { t } = useTranslation(PRODUCT);
  const userContext = useContext(UserContext);
  const { records, loading, className, onRefresh, pagination, setLoading } = props;

  const handleEdit = (record: ShopType) => {
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('popup.editFinal')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        setLoading && setLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              props.setDataFinalTile(record);
              props.setIsOpenModal(true);
              setLoading && setLoading(false);
            })
            .catch((e: any) => {
              message.error(e?.errorMessage);
              setLoading(false);
            });
        }
      },
      onCancel() {
        // NOT
      },
    });
  };

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledProductTable
          dataSource={records}
          loading={loading}
          className={className}
          rowKey="index"
          pagination={pagination}
        >
          <Column
            title={t('table.title.index')}
            align="center"
            dataIndex="index"
            key="index"
            render={value => <IndexColumn value={value} />}
            width="60px"
          />
          <Column
            title={t('table.title.show')}
            align="center"
            width={100}
            dataIndex="show"
            key="show"
            render={(_, record: ShopType) => <ShowHideColumnFinalTile onRefresh={onRefresh} record={record} />}
          />
          <Column
            title={t('table.title.title')}
            align="left"
            dataIndex="productName"
            key="productName"
            render={(value: any, record: ShopType) => <TitleColumn record={record} />}
            width="50%"
          />
          <Column
            title={t('table.title.action')}
            align="left"
            key="action"
            width={100}
            render={(_, record: ShopType) => (
              <Tooltip title={t('table.tooltip.edit')}>
                <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
              </Tooltip>
            )}
          />
        </StyledProductTable>
      </DndProvider>
    </>
  );
};

const StyledProductTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .ant-pagination-item a {
    line-height: 30px;
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }
`;
